import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Download, TrendingUp, Calendar, MapPin, Clock, AlertTriangle, Filter, Grid3x3, Gauge, RefreshCw } from 'lucide-react';
import { analyticsApi, schoolsApi, roomsApi } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { SchoolSelector } from '@/components/SchoolSelector';

const COLORS = {
  vape: '#ef4444',
  suspect: '#f59e0b', 
  offline: '#6b7280',
  normal: '#10b981'
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAYS = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];

function HeatmapGrid({ data }) {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-400 text-center py-8 font-ibm">Ingen data för vald period</p>;
  }
  const maxCount = Math.max(1, ...data.map(d => d.count));
  const lookup = {};
  data.forEach(d => { lookup[`${d.day_index}-${d.hour}`] = d.count; });

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Hour labels */}
        <div className="flex">
          <div className="w-10 shrink-0" />
          {HOURS.map(h => (
            <div key={h} className="flex-1 text-center text-[10px] text-slate-400 font-ibm pb-1">
              {h.toString().padStart(2, '0')}
            </div>
          ))}
        </div>
        {/* Day rows */}
        {[2, 3, 4, 5, 6, 7, 1].map(dow => (
          <div key={dow} className="flex items-center gap-0.5 mb-0.5">
            <div className="w-10 shrink-0 text-[11px] text-slate-500 font-ibm font-medium text-right pr-2">
              {DAYS[dow - 1]}
            </div>
            {HOURS.map(h => {
              const count = lookup[`${dow}-${h}`] || 0;
              const intensity = count / maxCount;
              return (
                <div
                  key={h}
                  className="flex-1 aspect-square rounded-[3px] transition-colors cursor-default"
                  style={{
                    backgroundColor: count === 0
                      ? '#f1f5f9'
                      : `rgba(239, 68, 68, ${0.15 + intensity * 0.85})`,
                  }}
                  title={`${DAYS[dow - 1]} kl ${h}:00 — ${count} larm`}
                  data-testid={`heatmap-cell-${dow}-${h}`}
                />
              );
            })}
          </div>
        ))}
        {/* Legend */}
        <div className="flex items-center justify-end gap-2 mt-3">
          <span className="text-[10px] text-slate-400 font-ibm">Färre</span>
          {[0, 0.25, 0.5, 0.75, 1].map((v, i) => (
            <div
              key={i}
              className="w-4 h-4 rounded-[2px]"
              style={{ backgroundColor: v === 0 ? '#f1f5f9' : `rgba(239, 68, 68, ${0.15 + v * 0.85})` }}
            />
          ))}
          <span className="text-[10px] text-slate-400 font-ibm">Fler</span>
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [period, setPeriod] = useState('30');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [roomFilter, setRoomFilter] = useState('all');
  const [schools, setSchools] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  const [eventsByDay, setEventsByDay] = useState([]);
  const [eventsBySchool, setEventsBySchool] = useState([]);
  const [eventsByRoom, setEventsByRoom] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [calibration, setCalibration] = useState(null);

  useEffect(() => {
    loadSchools();
  }, []);

  useEffect(() => {
    if (schoolFilter !== 'all') {
      loadRooms(schoolFilter);
    } else {
      setRooms([]);
      setRoomFilter('all');
    }
  }, [schoolFilter]);

  useEffect(() => {
    loadData();
  }, [period, schoolFilter, roomFilter, dateFrom, dateTo]);

  const loadSchools = async () => {
    try {
      const res = await schoolsApi.list();
      setSchools(res.data);
    } catch (error) {
      console.error('Error loading schools:', error);
    }
  };

  const loadRooms = async (schoolId) => {
    try {
      const res = await roomsApi.list();
      setRooms(res.data.filter(r => r.school_id === schoolId));
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { days: parseInt(period) };
      if (schoolFilter !== 'all') params.school_id = schoolFilter;
      
      const [dayRes, schoolRes, roomRes, hourlyRes, heatmapRes, calRes] = await Promise.all([
        analyticsApi.eventsByDay(params),
        analyticsApi.eventsBySchool(params),
        analyticsApi.eventsByRoom(params),
        analyticsApi.hourlyDistribution(params),
        analyticsApi.heatmap(params),
        analyticsApi.calibration().catch(() => ({ data: null })),
      ]);
      
      setEventsByDay(dayRes.data);
      setEventsBySchool(schoolRes.data);
      setEventsByRoom(roomRes.data);
      setHourlyData(hourlyRes.data);
      setHeatmapData(heatmapRes.data);
      if (calRes.data) setCalibration(calRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Kunde inte ladda analysdata');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await analyticsApi.exportEvents({ days: parseInt(period) });
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vapealarm_events_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Rapport exporterad');
    } catch (error) {
      toast.error('Kunde inte exportera rapport');
    }
  };

  const handleExportPdf = async () => {
    try {
      const params = { days: parseInt(period) };
      if (schoolFilter !== 'all') params.school_id = schoolFilter;
      
      const response = await analyticsApi.exportPdf(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vapealarm_rapport_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF-rapport exporterad');
    } catch (error) {
      toast.error('Kunde inte exportera PDF');
    }
  };

  // Calculate totals
  const totalVape = eventsByDay.reduce((sum, d) => sum + (d.vape_count || 0), 0);
  const totalSuspect = eventsByDay.reduce((sum, d) => sum + (d.suspect_count || 0), 0);
  const totalEvents = eventsByDay.reduce((sum, d) => sum + (d.total || 0), 0);

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar analysdata...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
            Analys & Rapporter
          </h1>
          <p className="text-slate-500 mt-1 font-ibm">
            Historisk data och trender
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={period} onValueChange={(v) => { setPeriod(v); setDateFrom(''); setDateTo(''); }}>
            <SelectTrigger className="w-[150px]" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Senaste 7 dagar</SelectItem>
              <SelectItem value="30">Senaste 30 dagar</SelectItem>
              <SelectItem value="90">Senaste 90 dagar</SelectItem>
              <SelectItem value="365">Senaste året</SelectItem>
              <SelectItem value="custom">Anpassat</SelectItem>
            </SelectContent>
          </Select>

          {period === 'custom' && (
            <>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} 
                className="w-[150px]" data-testid="date-from" />
              <span className="text-slate-400 text-sm">till</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} 
                className="w-[150px]" data-testid="date-to" />
            </>
          )}
          
          <SchoolSelector schools={schools} value={schoolFilter} onChange={setSchoolFilter} triggerClassName="w-[160px]" />

          {rooms.length > 0 && (
            <Select value={roomFilter} onValueChange={setRoomFilter}>
              <SelectTrigger className="w-[160px]" data-testid="room-analytics-filter">
                <SelectValue placeholder="Alla rum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla rum</SelectItem>
                {rooms.map(room => (
                  <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Button onClick={handleExport} variant="outline" className="font-ibm" data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
          
          <Button onClick={handleExportPdf} className="bg-navy hover:bg-navy/90 font-ibm" data-testid="export-pdf-btn">
            <Download className="h-4 w-4 mr-2" /> PDF Rapport
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-red-700 font-ibm">Vape-larm</p>
                <p className="text-3xl font-chivo font-black text-red-800">{totalVape}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-700 font-ibm">Misstänkta</p>
                <p className="text-3xl font-chivo font-black text-amber-800">{totalSuspect}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600 font-ibm">Totalt händelser</p>
                <p className="text-3xl font-chivo font-black text-slate-800">{totalEvents}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Events Over Time Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="font-chivo text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Händelser över tid
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={eventsByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(val) => val.slice(5)}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar dataKey="vape_count" stackId="a" fill={COLORS.vape} name="Vape" />
                <Bar dataKey="suspect_count" stackId="a" fill={COLORS.suspect} name="Misstänkt" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Fördelning per timme
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 10 }}
                    interval={2}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#1e3a5f" 
                    strokeWidth={2}
                    dot={{ fill: '#1e3a5f', r: 3 }}
                    name="Larm"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center font-ibm">
              Visar vape- och misstänkta händelser per timme
            </p>
          </CardContent>
        </Card>

        {/* Room Heatmap */}
        <Card>
          <CardHeader>
            <CardTitle className="font-chivo text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Rum med flest larm
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {eventsByRoom.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8 font-ibm">
                  Ingen data för vald period
                </p>
              ) : (
                eventsByRoom.map((room, idx) => (
                  <div 
                    key={room.room_id}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg",
                      room.severity === 'high' && "bg-red-50 border border-red-200",
                      room.severity === 'medium' && "bg-amber-50 border border-amber-200",
                      room.severity === 'low' && "bg-slate-50 border border-slate-200"
                    )}
                  >
                    <div>
                      <p className="font-ibm font-semibold text-sm">{room.room_name}</p>
                      <p className="text-xs text-slate-500">{room.school_name}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
                          {room.vape_count} vape
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                          {room.suspect_count} misstänkt
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{room.total_events} totalt</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Events by School */}
      {eventsBySchool.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-chivo text-lg">Händelser per skola</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={eventsBySchool} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis 
                    dataKey="school_name" 
                    type="category" 
                    tick={{ fontSize: 11 }}
                    width={120}
                  />
                  <Tooltip />
                  <Bar dataKey="vape" fill={COLORS.vape} name="Vape" />
                  <Bar dataKey="suspect" fill={COLORS.suspect} name="Misstänkt" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Heatmap: Weekday × Hour */}
      <Card data-testid="heatmap-card">
        <CardHeader>
          <CardTitle className="font-chivo text-lg flex items-center gap-2">
            <Grid3x3 className="h-5 w-5" />
            Heatmap — Veckodag & Tid
          </CardTitle>
        </CardHeader>
        <CardContent>
          <HeatmapGrid data={heatmapData} />
          <p className="text-xs text-slate-500 mt-3 text-center font-ibm">
            Mörk = fler larm. Visar vape + misstänkta händelser per veckodag och timme.
          </p>
        </CardContent>
      </Card>

      {/* Confidence Calibration */}
      {calibration && (
        <Card data-testid="calibration-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-chivo text-lg flex items-center gap-2">
                <Gauge className="h-5 w-5" />
                Konfidenskalibrering
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await analyticsApi.recalibrate();
                    const res = await analyticsApi.calibration();
                    setCalibration(res.data);
                    toast.success('Kalibrering uppdaterad');
                  } catch { toast.error('Kunde inte kalibrera om'); }
                }}
                data-testid="recalibrate-btn"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                Kalibrera om
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className={cn(
                  "text-2xl font-chivo font-black",
                  calibration.is_active ? "text-emerald-600" : "text-slate-400"
                )}>
                  {calibration.is_active ? 'AKTIV' : 'INAKTIV'}
                </p>
                <p className="text-xs text-slate-500 font-ibm">Status</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-chivo font-black text-navy dark:text-white">
                  {calibration.total_feedback}
                </p>
                <p className="text-xs text-slate-500 font-ibm">Feedback-händelser</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-chivo font-black text-navy dark:text-white">
                  {calibration.global_accuracy != null
                    ? `${Math.round(calibration.global_accuracy * 100)}%`
                    : '–'}
                </p>
                <p className="text-xs text-slate-500 font-ibm">Global träffsäkerhet</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-400 font-ibm mt-1">
                  {calibration.last_calibrated
                    ? `Senast: ${new Date(calibration.last_calibrated).toLocaleString('sv-SE')}`
                    : 'Inte kalibrerad än'}
                </p>
              </div>
            </div>

            {!calibration.is_active && (
              <div className="text-center py-2 px-4 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-sm font-ibm">
                Behöver minst {calibration.min_feedback_required} feedback-händelser för att aktivera kalibrering.
                Nuvarande: {calibration.total_feedback}.
              </div>
            )}

            {/* Band breakdown */}
            {calibration.is_active && calibration.bands && (
              <div>
                <h4 className="text-sm font-ibm font-semibold text-slate-600 dark:text-slate-300 mb-3 uppercase tracking-wide">
                  Träffsäkerhet per konfidens-band
                </h4>
                <div className="space-y-2">
                  {Object.values(calibration.bands).map((band, idx) => {
                    const lo = band.band?.[0] ?? 0;
                    const hi = band.band?.[1] ?? 1;
                    const pct = band.accuracy != null ? Math.round(band.accuracy * 100) : null;
                    const factor = band.factor ?? 1.0;
                    return (
                      <div key={idx} className="flex items-center gap-3" data-testid={`calibration-band-${idx}`}>
                        <div className="w-28 text-xs font-ibm font-medium text-slate-500 shrink-0">
                          {Math.round(lo * 100)}–{Math.round(hi * 100)}%
                        </div>
                        <div className="flex-1 h-6 bg-slate-100 dark:bg-slate-800 rounded-sm overflow-hidden relative">
                          {pct != null && (
                            <div
                              className={cn(
                                "h-full rounded-sm transition-all",
                                pct >= 70 ? "bg-emerald-500" :
                                pct >= 40 ? "bg-amber-500" :
                                "bg-red-500"
                              )}
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          )}
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-slate-700 dark:text-slate-200">
                            {pct != null ? `${pct}% träff` : 'Ej tillräckligt data'}
                            {band.total > 0 ? ` (${band.confirmed}/${band.total})` : ''}
                          </span>
                        </div>
                        <div className={cn(
                          "w-16 text-xs font-ibm font-semibold text-right shrink-0",
                          factor > 1.05 ? "text-emerald-600" :
                          factor < 0.95 ? "text-red-600" :
                          "text-slate-500"
                        )}>
                          ×{factor.toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-400 mt-3 font-ibm">
                  Faktor {'>'} 1.0 = systemet var för försiktigt (höjer poängen). Faktor {'<'} 1.0 = systemet var för aggressivt (sänker poängen).
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
